<template>
<div v-if="client != false" class="mt-app" :class="{ 'mt-has-bottombar': hasBottombar }">

  <!-- ============== HEADER ============== -->
  <header class="mt-header">
    <div class="mt-header-inner">
      <div class="mt-header-brand">
        <b-img :src="$api.ste.logo" alt="Logo" class="mt-logo" />
      </div>

      <!-- Stepper -->
      <nav class="mt-stepper" aria-label="Progression du parcours">
        <div class="mt-step" :class="{ active: stepIndex === 0, done: stepIndex > 0, clickable: stepIndex > 0 }"
             @click="stepIndex > 0 && (currentStep = 'infos')">
          <div class="mt-step-bullet">
            <i v-if="stepIndex > 0" class="fas fa-check text-white"></i>
            <span v-else>1</span>
          </div>
          <span class="mt-step-label">Mes infos</span>
        </div>
        <div class="mt-step-bar"><div class="mt-step-bar-fill" :class="{ active: stepIndex > 0 }"></div></div>
        <div class="mt-step" :class="{ active: stepIndex === 1, done: stepIndex > 1, clickable: stepIndex > 1 }"
             @click="stepIndex > 1 && (currentStep = 'search')">
          <div class="mt-step-bullet">
            <i v-if="stepIndex > 1" class="fas fa-check text-white"></i>
            <span v-else>2</span>
          </div>
          <span class="mt-step-label">Mes offres</span>
        </div>
        <div class="mt-step-bar"><div class="mt-step-bar-fill" :class="{ active: stepIndex > 1 }"></div></div>
        <div class="mt-step" :class="{ active: stepIndex === 2, done: stepIndex > 2, clickable: stepIndex > 2 }"
             @click="stepIndex > 2 && (currentStep = 'devis')">
          <div class="mt-step-bullet">
            <i v-if="stepIndex > 2" class="fas fa-check text-white"></i>
            <span v-else>3</span>
          </div>
          <span class="mt-step-label">Mon devis</span>
        </div>
        <div class="mt-step-bar"><div class="mt-step-bar-fill" :class="{ active: stepIndex > 2 }"></div></div>
        <div class="mt-step" :class="{ active: stepIndex === 3 }">
          <div class="mt-step-bullet"><span>4</span></div>
          <span class="mt-step-label">Confirmation</span>
        </div>
      </nav>

      <!-- Conseiller -->
      <div class="mt-advisor">
        <b-img :src="$api.link(agent.avatar)" rounded="circle" alt="Conseiller" class="mt-advisor-avatar" />
        <div class="mt-advisor-text">
          <span class="mt-advisor-eyebrow">Votre conseiller</span>
          <span class="mt-advisor-name">{{ agent.prenom }}</span>
          <a :href="`tel:${agent.telephone}`" class="mt-advisor-phone">
            <i class="fas fa-phone-alt"></i> {{ agent.telephone }}
          </a>
        </div>
      </div>
    </div>
  </header>

  <LoaderWidget v-if="loadQuote" :mutuelles="mutuelles" :is-done="loadQuoteDone" @finished="onLoaderFinished" />

  <main class="mt-main">

    <!-- ============== ÉTAPE 1 : MES INFORMATIONS ============== -->
    <section v-if="currentStep === 'infos'" class="mt-infos">
      <div class="mt-infos-grid">
        <div class="mt-infos-main">
          <div class="mt-card mt-infos-card">
            <div class="mt-card-head">
              <span class="mt-eyebrow"><i class="fas fa-user-edit"></i> Étape 1 sur 4</span>
              <h1 class="mt-card-title">Vos informations</h1>
              <p class="mt-card-sub">
                Vérifiez ou complétez vos informations pour obtenir des offres précises et personnalisées.
              </p>
            </div>

            <b-form @submit.prevent="submitForm" class="mt-form mt-infos-form">

              <!-- Section 1 : Coordonnées -->
              <div class="mt-form-section">
                <h2 class="mt-form-section-title">
                  <span class="mt-form-section-num">1</span>
                  <i class="fas fa-id-card"></i> Vos coordonnées
                </h2>
                <div class="mt-grid-2">
                  <div class="mt-field">
                    <label class="mt-label">Nom <span class="mt-req">*</span></label>
                    <input v-model="client.nom" class="mt-input mt-input-bare" required />
                  </div>
                  <div class="mt-field">
                    <label class="mt-label">Prénom <span class="mt-req">*</span></label>
                    <input v-model="client.prenom" class="mt-input mt-input-bare" required />
                  </div>
                  <div class="mt-field">
                    <label class="mt-label">E-mail <span class="mt-req">*</span></label>
                    <input v-model.trim="client.email" type="email"
                           class="mt-input mt-input-bare"
                           :class="{
                             'is-invalid': emailState === false && client.email && client.email.length,
                             'is-valid': emailState === true
                           }"
                           placeholder="vous@email.fr" required />
                    <small class="mt-error" v-if="emailState === false && client.email && client.email.length">
                      {{ emailError }}
                    </small>
                  </div>
                  <div class="mt-field">
                    <label class="mt-label">Téléphone <span class="mt-req">*</span></label>
                    <input v-model="client.telephone_1" maxlength="10" class="mt-input mt-input-bare"
                           placeholder="06 12 34 56 78" inputmode="numeric" required />
                  </div>
                  <div class="mt-field">
                    <label class="mt-label">Code postal <span class="mt-req">*</span></label>
                    <div class="mt-autocomplete">
                      <input v-model="client.cp" class="mt-input mt-input-bare"
                             placeholder="75001" maxlength="5" inputmode="numeric" required
                             autocomplete="off"
                             @focus="showCitySuggestions = true"
                             @input="showCitySuggestions = true"
                             @blur="onCityBlur" />
                      <ul v-if="showCitySuggestions && cityOptions.length > 0"
                          class="mt-autocomplete-list">
                        <li v-for="(v, i) in cityOptions" :key="i"
                            class="mt-autocomplete-item"
                            @mousedown.prevent="selectCity(v)">
                          <i class="fas fa-map-marker-alt"></i>
                          <span>{{ v }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div class="mt-field">
                    <label class="mt-label">Ville <span class="mt-req">*</span></label>
                    <input v-model="client.ville" class="mt-input mt-input-bare"
                           placeholder="Ex : Paris" autocomplete="off" required />
                  </div>
                  <div class="mt-field">
                    <label class="mt-label">Date de naissance <span class="mt-req">*</span></label>
                    <input v-model="client.date_naissance" type="date" :max="limitAgeAdulte"
                           class="mt-input mt-input-bare" required />
                  </div>
                  <div class="mt-field">
                    <label class="mt-label">Régime de Sécurité Sociale <span class="mt-req">*</span></label>
                    <select v-model="client.regime_id" class="mt-input mt-input-bare" required>
                      <option :value="null">Sélectionnez votre régime</option>
                      <option v-for="(r, i) in params.Regime" :key="i" :value="r.id">{{ r.titre }}</option>
                    </select>
                  </div>
                  <div class="mt-field">
                    <label class="mt-label">Date d'effet souhaitée <span class="mt-req">*</span></label>
                    <input v-model="client.date_effet" type="date"
                           :min="$api.moment().format('YYYY-MM-DD')"
                           class="mt-input mt-input-bare" required />
                  </div>
                </div>
              </div>

              <!-- Section 2 : Conjoint -->
              <div class="mt-form-section">
                <h2 class="mt-form-section-title">
                  <span class="mt-form-section-num">2</span>
                  <i class="fas fa-user-friends"></i> Souhaitez-vous assurer votre conjoint&nbsp;?
                </h2>
                <div class="mt-toggle">
                  <button type="button" class="mt-toggle-btn" :class="{ active: wantsSpouse === true }"
                          @click="setWantsSpouse(true)">
                    <i class="fas fa-check"></i> Oui
                  </button>
                  <button type="button" class="mt-toggle-btn" :class="{ active: wantsSpouse === false }"
                          @click="setWantsSpouse(false)">
                    <i class="fas fa-times"></i> Non
                  </button>
                </div>

                <div v-if="wantsSpouse === true" class="mt-grid-2 mt-mt-3">
                  <div class="mt-field">
                    <label class="mt-label">Date de naissance du conjoint <span class="mt-req">*</span></label>
                    <input v-model="client.conjoint.date_naissance" type="date" :max="limitAgeAdulte"
                           class="mt-input mt-input-bare" required />
                  </div>
                  <div class="mt-field">
                    <label class="mt-label">Régime du conjoint <span class="mt-req">*</span></label>
                    <select v-model="client.conjoint.regime_id" class="mt-input mt-input-bare" required>
                      <option :value="null">Sélectionnez</option>
                      <option v-for="(r, i) in params.Regime" :key="i" :value="r.id">{{ r.titre }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Section 3 : Enfants -->
              <div class="mt-form-section">
                <h2 class="mt-form-section-title">
                  <span class="mt-form-section-num">3</span>
                  <i class="fas fa-child"></i> Souhaitez-vous assurer en plus vos enfants&nbsp;?
                </h2>
                <div class="mt-toggle">
                  <button type="button" class="mt-toggle-btn" :class="{ active: wantsChildren === true }"
                          @click="setWantsChildren(true)">
                    <i class="fas fa-check"></i> Oui
                  </button>
                  <button type="button" class="mt-toggle-btn" :class="{ active: wantsChildren === false }"
                          @click="setWantsChildren(false)">
                    <i class="fas fa-times"></i> Non
                  </button>
                </div>

                <div v-if="wantsChildren === true" class="mt-mt-3">
                  <div class="mt-children-actions">
                    <button type="button" class="mt-btn mt-btn-outline mt-btn-sm"
                            @click="client.enfants.push({date_naissance: null, regime_id: null})">
                      <i class="fas fa-plus"></i> Ajouter un enfant
                    </button>
                  </div>

                  <div v-if="client.enfants.length === 0" class="mt-empty-soft">
                    <i class="fas fa-info-circle"></i>
                    Cliquez sur « Ajouter un enfant » pour commencer.
                  </div>

                  <div v-for="(enfant, index) in client.enfants" :key="index" class="mt-child">
                    <div class="mt-child-head">
                      <span><i class="fas fa-child"></i> Enfant {{ index + 1 }}</span>
                      <button type="button" class="mt-child-remove"
                              @click="client.enfants.splice(index, 1)" aria-label="Supprimer">
                        <i class="fas fa-trash-alt"></i> Retirer
                      </button>
                    </div>
                    <div class="mt-grid-2">
                      <div class="mt-field">
                        <label class="mt-label">Date de naissance <span class="mt-req">*</span></label>
                        <input v-model="enfant.date_naissance" type="date"
                               :min="limitAgeEnfant" :max="$api.moment().format('YYYY-MM-DD')"
                               class="mt-input mt-input-bare" required />
                      </div>
                      <div class="mt-field">
                        <label class="mt-label">Régime <span class="mt-req">*</span></label>
                        <select v-model="enfant.regime_id" class="mt-input mt-input-bare" required>
                          <option :value="null">Sélectionnez</option>
                          <option v-for="(r, i) in params.Regime" :key="i" :value="r.id">{{ r.titre }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section 4 : Mes besoins -->
              <div class="mt-form-section">
                <h2 class="mt-form-section-title">
                  <span class="mt-form-section-num">4</span>
                  <i class="fas fa-sliders-h"></i> Vos besoins en santé
                </h2>
                <p class="mt-form-section-sub">
                  Indiquez le niveau de remboursement souhaité pour chaque poste de santé.
                  Plus le niveau est élevé, meilleurs sont les remboursements&nbsp;— et plus la cotisation peut augmenter.
                </p>
                <div class="mt-needs mt-needs-inline">
                  <div class="mt-need" v-for="(k, i) in Object.keys(filters)" :key="i">
                    <div class="mt-need-head">
                      <div class="mt-need-icon"><i :class="filters[k].icon"></i></div>
                      <span class="mt-need-label">{{ k }}</span>
                    </div>
                    <div class="mt-need-levels" role="group" :aria-label="`Niveau ${k}`">
                      <button type="button"
                              v-for="lvl in 4" :key="lvl"
                              class="mt-need-level"
                              :class="{ active: filters[k].value === lvl }"
                              @click="setNeedLevel(k, lvl)">
                        {{ ['Mini','Moyen','Fort','Maxi'][lvl-1] }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section 5 : Budget mensuel -->
              <!--
                Les champs sont partagés (v-model) avec le filtre "Filtrer par
                budget mensuel" de l'étape "Mes offres" : si le prospect saisit
                sa fourchette ici, elle est déjà active quand il arrive sur les
                offres, et il pourra l'ajuster à ce moment-là.
              -->
              <div class="mt-form-section">
                <h2 class="mt-form-section-title">
                  <span class="mt-form-section-num">5</span>
                  <i class="fas fa-euro-sign"></i> Votre budget mensuel
                </h2>
                <p class="mt-form-section-sub">
                  Indiquez la fourchette de budget que vous souhaitez consacrer à votre mutuelle.
                  Vous pourrez l'ajuster à tout moment sur la page des offres.
                </p>
                <div class="mt-grid-2">
                  <div class="mt-field">
                    <label for="infos-price-min" class="mt-label">Budget minimum</label>
                    <div class="mt-input-wrap">
                      <span class="mt-input-prefix">€</span>
                      <input id="infos-price-min" type="number" min="0" step="5"
                             v-model.number="priceMin"
                             class="mt-input" placeholder="Min"
                             required
                             @change="onPriceMinChange" />
                      <span class="mt-input-suffix">/ mois</span>
                    </div>
                  </div>
                  <div class="mt-field">
                    <label for="infos-price-max" class="mt-label">Budget maximum</label>
                    <div class="mt-input-wrap">
                      <span class="mt-input-prefix">€</span>
                      <input id="infos-price-max" type="number" min="0" step="5"
                             v-model.number="priceMax"
                             class="mt-input" placeholder="Max"
                             required
                             @change="onPriceMaxChange" />
                      <span class="mt-input-suffix">/ mois</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer CTA -->
              <div class="mt-infos-footer">
                <button type="submit" class="mt-btn mt-btn-primary mt-btn-lg mt-btn-block">
                  <i class="fas fa-search text-white"></i> Voir mes offres adaptées
                  <i class="fas fa-arrow-right text-white"></i>
                </button>
                <small class="mt-infos-footer-note">
                  <i class="fas fa-lock"></i>
                  Vos données sont sécurisées et utilisées uniquement pour votre devis
                </small>
              </div>
            </b-form>
          </div>
        </div>

        <!-- Aside conseiller + confiance -->
        <aside class="mt-infos-aside">
          <div class="mt-help-card">
            <div class="mt-help-head">
              <i class="fas fa-headset"></i>
              <span>Votre conseiller {{ agent.prenom }}</span>
            </div>
            <div class="mt-advisor-large">
              <b-img :src="$api.link(agent.avatar)" rounded="circle"
                     alt="Conseiller" class="mt-advisor-large-avatar" />
              <span class="mt-advisor-large-name">{{ agent.prenom }}</span>
              <span class="mt-advisor-large-role">Votre conseiller dédié</span>
            </div>
            <a :href="`tel:${agent.telephone}`" class="mt-help-phone">
              <i class="fas fa-phone-alt"></i> {{ agent.telephone }}
            </a>
            <small class="mt-help-hours">Du lundi au vendredi, 9h&nbsp;-&nbsp;19h</small>
          </div>

          <div class="mt-trust-card">
            <h4 class="mt-trust-card-title">Pourquoi nous sommes différents&nbsp;?</h4>
            <ul class="mt-trust-list">
              <li><i class="fas fa-gem"></i> Mutuelles exclusives</li>
              <li><i class="fas fa-shield-alt"></i> Données sécurisées (RGPD)</li>
              <li><i class="fas fa-user-tie"></i> Conseillers avec +15 ans d'expérience en moyenne</li>
              <li><i class="fas fa-user-check"></i> Conseiller dédié unique pendant toute la durée du contrat</li>
              <li><i class="fab fa-google"></i> Excellente note Google&nbsp;: 4,5&nbsp;/&nbsp;5</li>
              <li><i class="fas fa-certificate"></i> Avis vérifiés</li>
            </ul>
          </div>
        </aside>
      </div>
    </section>

    <!-- ============== ÉTAPE 2 : OFFRES ============== -->
    <section v-if="currentStep === 'search'" class="mt-search">

      <!-- HERO -->
      <div class="mt-hero">
        <div class="mt-hero-left">
          <span class="mt-eyebrow"><i class="fas fa-shield-alt"></i> Comparateur Mutuelle Santé</span>
          <h1 class="mt-h1">
            Bonjour <span class="mt-h1-name">{{ client.prenom }}</span>,<br/>
            voici <strong>{{ datatable.totalRow }} offre{{ datatable.totalRow > 1 ? 's' : '' }}</strong>
            adaptée{{ datatable.totalRow > 1 ? 's' : '' }} à votre profil
          </h1>
          <p class="mt-h1-sub">
            Voici les offres les plus proches et les plus adéquates entre vos besoins et le budget sélectionné.<br/>
            Tarifs calculés pour une prise d'effet au
            <strong>{{ $api.moment(client.date_effet).format("DD/MM/YYYY") }}</strong>.
          </p>
          <div class="mt-trust">
            <span class="mt-trust-item"><i class="fas fa-check-circle"></i> 100&nbsp;% gratuit</span>
            <span class="mt-trust-item"><i class="fas fa-check-circle"></i> Sans engagement</span>
            <span class="mt-trust-item"><i class="fas fa-lock"></i> Données sécurisées</span>
          </div>
          <div class="mt-marketing-bullets">
            <span class="mt-marketing-bullet"><i class="fas fa-user-tie"></i> Conseiller dédié avec ligne directe de 9h à 19h</span>
            <span class="mt-marketing-bullet"><i class="fas fa-handshake"></i> Le même conseiller pendant toute la durée du contrat</span>
            <span class="mt-marketing-bullet"><i class="fab fa-google"></i> Excellente note Google : 4,5 / 5</span>
            <span class="mt-marketing-bullet"><i class="fas fa-user-md"></i> Vous êtes suivi par un conseiller ayant plus de 15 années d'expérience en mutuelle santé</span>
          </div>
        </div>
        <div class="mt-hero-right">
          <div class="mt-help-card">
            <div class="mt-help-head">
              <i class="fas fa-headset"></i>
              <span>Votre conseiller {{ agent.prenom }}</span>
            </div>
            <p>Bonjour, je suis {{ agent.prenom }}. Contactez-moi &nbsp;:</p>
            <a :href="`tel:${agent.telephone}`" class="mt-help-phone">
              <i class="fas fa-phone-alt text-white"></i> {{ agent.telephone }}
            </a>
            <small class="mt-help-hours">Du lundi au vendredi, 9h&nbsp;-&nbsp;19h</small>
          </div>
        </div>
      </div>

      <!-- FILTRE PRIX -->
      <div class="mt-price-filter mt-price-filter-inline">
        <h3 class="mt-price-filter-title">
          <i class="fas fa-euro-sign"></i> Filtrer par budget mensuel
        </h3>
        <div class="mt-price-fields-inline">
          <div class="mt-input-wrap mt-price-input-wrap">
            <span class="mt-input-prefix">€</span>
            <input type="number" min="0" step="5"
                   v-model.number="priceMin"
                   class="mt-input"
                   placeholder="Min"
                   aria-label="Budget minimum"
                   @input="onPriceInput"
                   @change="onPriceMinChange" />
          </div>
          <span class="mt-price-sep" aria-hidden="true">→</span>
          <div class="mt-input-wrap mt-price-input-wrap">
            <span class="mt-input-prefix">€</span>
            <input type="number" min="0" step="5"
                   v-model.number="priceMax"
                   class="mt-input"
                   placeholder="Max"
                   aria-label="Budget maximum"
                   @input="onPriceInput"
                   @change="onPriceMaxChange" />
            <span class="mt-input-suffix">/ mois</span>
          </div>
        </div>
        <p v-if="priceRangeInvalid" class="mt-price-hint">
          <i class="fas fa-info-circle"></i>
          Votre budget maximum est inférieur au minimum : aucune offre ne peut correspondre.
        </p>
      </div>

      <!-- BANDEAU BESOINS SÉLECTIONNÉS (toujours visible au-dessus des offres) -->
      <div class="mt-needs-bar">
        <div class="mt-needs-bar-head">
          <span class="mt-needs-bar-title">
            <i class="fas fa-sliders-h"></i> Vos besoins
          </span>
          <button type="button" class="mt-needs-bar-edit" @click="openNeedsModal">
            <i class="fas fa-pen"></i> Modifier mes besoins
          </button>
        </div>
        <div class="mt-needs-bar-grid">
          <button type="button"
                  v-for="(k, i) in Object.keys(filters)" :key="i"
                  class="mt-needs-bar-item"
                  :class="{ 'is-empty': filters[k].value === null }"
                  @click="openNeedsModal"
                  :aria-label="`Modifier le niveau ${k}`">
            <span class="mt-needs-bar-icon"><i :class="filters[k].icon"></i></span>
            <span class="mt-needs-bar-text">
              <span class="mt-needs-bar-label">{{ k }}</span>
              <span class="mt-needs-bar-level">{{ filters[k].value === null ? 'Tous niveaux' : ['Mini','Moyen','Fort','Maxi'][filters[k].value - 1] }}</span>
            </span>
          </button>
        </div>
      </div>

      <!-- LISTE OFFRES -->
      <div class="mt-results">
        <div class="mt-results-head">
          <h2 class="mt-results-title">
            <span class="mt-results-count">{{ datatable.totalRow }}</span>
            offre{{ datatable.totalRow > 1 ? 's' : '' }} {{ datatable.totalRow > 1 ? 'trouvées' : 'trouvée' }}
          </h2>
          <div class="mt-results-actions">
            <button type="button" class="mt-btn mt-btn-outline" @click="currentStep = 'infos'">
              <i class="fas fa-user-edit"></i>
              Modifier mes infos
            </button>
            <!-- Affiché uniquement en mobile (le bandeau "Vos besoins" prend le relais en desktop) -->
            <button type="button" class="mt-btn mt-btn-outline mt-btn-needs-mobile" @click="openNeedsModal">
              <i class="fas fa-sliders-h"></i>
              Modifier mes besoins
              <span v-if="activeFiltersCount > 0" class="mt-badge-pill">{{ activeFiltersCount }}</span>
            </button>
            <button type="button" class="mt-btn mt-btn-ghost" @click="setSort()">
              <i class="fas" :class="sort.value == 'DESC' ? 'fa-sort-amount-down' : 'fa-sort-amount-up'"></i>
              Prix {{ sort.value == 'DESC' ? 'décroissant' : 'croissant' }}
            </button>
          </div>
        </div>

        <div v-if="price_load === false">
          <article class="mt-offer" v-for="(o, i) in datatable.data" :key="i" :class="{ selected: checkQuote(o) }">
            <transition name="mt-offer-badge-anim">
              <span v-if="checkQuote(o)" class="mt-offer-selected-badge" aria-label="Offre sélectionnée">
                <i class="fas fa-check-circle text-white"></i> Offre sélectionnée
              </span>
            </transition>
            <div class="mt-offer-grid">
              <!-- Colonne 1 : Marque + Prix -->
              <div class="mt-offer-brand">
                <span class="mt-offer-formule">{{ o.formule }}</span>
                <span class="mt-offer-product">{{ o.produit }}</span>
                <div class="mt-offer-price">
                  <span class="mt-offer-price-amount">{{ $api.price(o.tarif) }}</span>
                  <span class="mt-offer-price-unit">/ mois</span>
                </div>
                <span class="mt-offer-badge" :class="o.responsable ? 'is-ok' : 'is-warn'">
                  <i :class="o.responsable ? 'fas fa-check-circle' : 'fas fa-info-circle'"></i>
                  Formule {{ o.responsable ? 'responsable' : 'non responsable' }}
                </span>
                <span v-if="o.responsable" class="mt-offer-badge mt-offer-badge-sante"
                      title="Cette formule intègre les garanties 100 % Santé (optique, dentaire, audiologie sans reste à charge)">
                  <i class="fas fa-heartbeat"></i>
                  100&nbsp;% Santé
                </span>
              </div>

              <!-- Colonne 2 : Garanties -->
              <div class="mt-offer-coverage">
                <span class="mt-offer-coverage-title">Niveau de remboursement</span>
                <div class="mt-coverage-list">
                  <div class="mt-coverage-row" v-for="(k, idx) in Object.keys(filters)" :key="idx">
                    <div class="mt-coverage-label">
                      <i :class="filters[k].icon"></i>
                      <span>{{ k }}</span>
                    </div>
                    <div class="mt-coverage-bar" :aria-label="`${k} niveau ${o.site[k] || 0} sur 4`">
                      <span class="mt-coverage-cell" v-for="lvl in 4" :key="lvl"
                            :class="{ filled: o.site[k] >= lvl, ['lvl-' + lvl]: o.site[k] >= lvl }"></span>
                    </div>
                    <span class="mt-coverage-tag">{{ coverageLabel(o.site[k]) }}</span>
                  </div>
                </div>

                <!-- Résumé des garanties (visible directement, sans clic) -->
                <template v-if="getResume(o.garantie).length > 0">
                  <span class="mt-offer-coverage-title mt-offer-resume-title">Résumé des garanties</span>
                  <ul class="mt-resume-list">
                    <li v-for="(r, ri) in getResume(o.garantie)" :key="ri" class="mt-resume-item">
                      <span class="mt-resume-label">{{ r.key }}</span>
                      <span class="mt-resume-value"
                            v-b-tooltip.hover
                            :class="{ 'is-truncated': isLong(r.value) }"
                            :title="isLong(r.value) ? r.value : null">
                        {{ truncate(r.value) }}
                      </span>
                    </li>
                  </ul>
                </template>
              </div>

              <!-- Colonne 3 : Actions -->
              <div class="mt-offer-actions">
                <!-- 1. Souscrire (CTA principal) -->
                <button type="button"
                        class="mt-btn mt-btn-primary mt-btn-block mt-btn-lg"
                        @click="requestCallback(o, 'souscription')">
                  <i class="fas fa-handshake text-white"></i>
                  Souscrire
                </button>

                <!-- 2. Je suis intéressé -->
                <button type="button"
                        class="mt-btn mt-btn-outline mt-btn-block"
                        @click="requestCallback(o, 'interet')">
                  <i class="fas fa-star"></i>
                  Je suis intéressé
                </button>

                <!-- 3. Sélectionner / Retirer l'offre (panier) -->
                <button type="button"
                        class="mt-btn mt-btn-outline mt-btn-block"
                        :class="{ 'mt-btn-success': checkQuote(o) }"
                        @click="toggleQuoteWithAnimation(o, $event)">
                  <i :class="checkQuote(o) ? 'fas fa-times-circle text-danger' : 'fas fa-shopping-bag'"></i>
                  {{ checkQuote(o) ? "Retirer l'offre" : "Sélectionner l'offre" }}
                </button>

                <!-- 4. Être rappelé -->
                <button type="button" class="mt-btn mt-btn-outline mt-btn-block"
                        @click="requestCallback(o, 'rappel')">
                  <i class="fas fa-phone-alt"></i> Être rappelé
                </button>

                <!-- 5. Plus d'informations -->
                <button type="button" class="mt-btn mt-btn-link mt-btn-block" v-b-toggle="`docs-${o.id}`">
                  <i class="fas fa-info-circle"></i>
                  Plus d'informations
                </button>
              </div>
            </div>

            <!-- Plus d'informations : documents disponibles -->
            <b-collapse :id="`docs-${o.id}`" class="mt-offer-docs">
              <h4 class="mt-offer-docs-title">
                <i class="fas fa-folder-open"></i> Documents disponibles
              </h4>
              <div class="mt-offer-docs-grid">
                <a v-for="(d, id) in orderedDocs(o.docs)" :key="id"
                   :href="$api.link(d.value)" target="_blank" class="mt-doc">
                  <i class="fas fa-file-pdf"></i>
                  <span>{{ docLabel(d.key) }}</span>
                  <i class="fas fa-external-link-alt mt-doc-out"></i>
                </a>
              </div>
            </b-collapse>
          </article>

          <div v-if="datatable.data.length === 0" class="mt-empty">
            <i class="fas fa-search"></i>
            <h3>Nos conseillers reviennent vers vous</h3>
            <p>
              Aucune offre n'est disponible pour le moment dans notre catalogue.
              Un conseiller va vous contacter pour vous proposer la meilleure solution.
            </p>
            <a :href="`tel:${agent.telephone}`" class="mt-btn mt-btn-primary mt-btn-lg mt-mt-3">
              <i class="fas fa-phone-alt text-white"></i> Appeler {{ agent.prenom }} maintenant
            </a>
          </div>
        </div>

        <!-- Loader -->
        <div v-else class="mt-loading">
          <div class="mt-loading-card" v-for="n in 3" :key="n">
            <div class="mt-skeleton mt-skeleton-block"></div>
            <div class="mt-skeleton mt-skeleton-line"></div>
            <div class="mt-skeleton mt-skeleton-line short"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============== ÉTAPE 2 : DEVIS ============== -->
    <section v-if="currentStep === 'devis'" class="mt-quote">
      <div class="mt-quote-centered">

        <!-- Bouton "Retour aux offres" — visible en haut -->
        <div class="mt-quote-back-top-wrap">
          <button type="button" class="mt-btn mt-btn-outline mt-quote-back-top" @click="currentStep = 'search'">
            <i class="fas fa-arrow-left"></i> Retour aux offres
          </button>
        </div>

        <div class="mt-card mt-recap">
          <div class="mt-card-head">
            <h2 class="mt-card-title">Recevoir mon devis personnalisé</h2>
            <p class="mt-card-sub">Recevez votre devis détaillé en moins de 2 minutes par e-mail.</p>
          </div>

          <h3 class="mt-recap-title">
            <i class="fas fa-shopping-bag"></i>
            {{ selected.length }} offre{{ selected.length > 1 ? 's' : '' }} sélectionnée{{ selected.length > 1 ? 's' : '' }}
          </h3>
          <ul class="mt-recap-list">
            <li v-for="(o, i) in selected" :key="i" class="mt-recap-item">
              <div class="mt-recap-info">
                <span class="mt-recap-product">{{ o.produit }}</span>
                <span class="mt-recap-formule">{{ o.formule }}</span>
              </div>
              <div class="mt-recap-price">
                <strong>{{ $api.price(o.tarif) }}</strong>
                <small>/ mois</small>
              </div>
              <button type="button"
                      class="mt-recap-remove" @click="addQuote(o)" aria-label="Retirer cette offre">
                <i class="fas fa-trash-alt"></i>
              </button>
            </li>
          </ul>

          <div class="mt-recap-help">
            <i class="fas fa-headset"></i>
            <div>
              <span>Une question&nbsp;?</span>
              <strong class="mt-recap-help-name">{{ agent.prenom }}</strong>
              <a :href="`tel:${agent.telephone}`">{{ agent.telephone }}</a>
            </div>
          </div>

          <b-form @submit.prevent="submitQuoteRequest" class="mt-quote-form">
            <!-- Coordonnées : pré-remplies depuis l'étape 1, modifiables ici si besoin -->
            <div class="mt-quote-fields">
              <div class="mt-field">
                <label for="quote-email" class="mt-label">
                  Adresse e-mail <span class="mt-req">*</span>
                </label>
                <div class="mt-input-wrap"
                     :class="{
                       'is-invalid': emailState === false && client.email && client.email.length,
                       'is-valid':   emailState === true
                     }">
                  <i class="fas fa-envelope mt-input-icon"></i>
                  <input id="quote-email" v-model.trim="client.email" type="email"
                         class="mt-input" placeholder="vous@email.fr"
                         autocomplete="email" required />
                </div>
                <small class="mt-error"
                       v-if="emailState === false && client.email && client.email.length">
                  {{ emailError }}
                </small>
              </div>

              <div class="mt-field">
                <label for="quote-phone" class="mt-label">
                  Téléphone <span class="mt-req">*</span>
                </label>
                <div class="mt-input-wrap"
                     :class="{
                       'is-invalid': phoneState === false && client.telephone_1 && client.telephone_1.length,
                       'is-valid':   phoneState === true
                     }">
                  <i class="fas fa-phone-alt mt-input-icon"></i>
                  <input id="quote-phone" v-model.trim="client.telephone_1" type="tel"
                         inputmode="numeric" maxlength="10"
                         class="mt-input" placeholder="06 12 34 56 78"
                         autocomplete="tel" required />
                </div>
                <small class="mt-error"
                       v-if="phoneState === false && client.telephone_1 && client.telephone_1.length">
                  {{ phoneError }}
                </small>
              </div>
            </div>

            <label class="mt-check">
              <input type="checkbox" v-model="quoteForm.optin" :true-value="true" :false-value="false" required />
              <span class="mt-check-mark"><i class="fas fa-check"></i></span>
              <span class="mt-check-text">
                En cliquant sur «&nbsp;recevoir mon devis par email&nbsp;», vous acceptez que vos données soient utilisées par
                <strong>l'Assureur Français</strong> pour vous contacter par e-mail et par téléphone à propos de
                votre demande de devis. Retrouvez plus d'informations sur le traitement de vos données dans
                notre
                <a href="https://assureur-francais.fr/mentions-legales/" target="_blank" rel="noopener">politique données personnelles</a>.
              </span>
            </label>

            <div class="mt-quote-actions-row">
              <button type="button" class="mt-btn mt-btn-outline mt-quote-back-bottom" @click="currentStep = 'search'">
                <i class="fas fa-arrow-left"></i> Retour aux offres
              </button>
              <button type="submit" class="mt-btn mt-btn-primary mt-btn-lg"
                      :disabled="sendLoad || selected.length === 0 || emailState !== true || phoneState !== true">
                <i v-if="sendLoad" class="fas fa-spin fa-spinner"></i>
                <i v-else class="fas fa-paper-plane text-white"></i>
                Recevoir mon devis par email
              </button>
            </div>
          </b-form>
        </div>
      </div>
    </section>

    <!-- ============== ÉTAPE 3 : MERCI ============== -->
    <section v-if="currentStep === 'merci'" class="mt-thanks">
      <div class="mt-card mt-thanks-card">
        <div class="mt-thanks-icon"><i class="fas fa-check text-white"></i></div>
        <h2 class="mt-thanks-title">Merci {{ client.prenom }}&nbsp;!</h2>
        <p class="mt-thanks-text">
          Votre demande a bien été enregistrée.<br/>
          Un e-mail récapitulatif vient d'être envoyé à
          <strong>{{ client.email }}</strong>
        </p>
        <p class="mt-thanks-spam">
          <i class="fas fa-info-circle"></i>
          Pensez à vérifier vos courriers indésirables (spams) si vous ne recevez pas l'e-mail dans les prochaines minutes.
        </p>

        <div class="mt-thanks-steps" v-if="false">
          <div class="mt-thanks-step">
            <div class="mt-thanks-step-num">1</div>
            <strong>Consultez votre boîte mail</strong>
            <span>Vous recevez immédiatement votre devis détaillé</span>
          </div>
          <div class="mt-thanks-step">
            <div class="mt-thanks-step-num">2</div>
            <strong>{{ agent.prenom }} vous appelle</strong>
            <span>Pour répondre à toutes vos questions</span>
          </div>
          <div class="mt-thanks-step">
            <div class="mt-thanks-step-num">3</div>
            <strong>Souscription en ligne</strong>
            <span>Profitez de votre nouvelle mutuelle</span>
          </div>
        </div>

        <div class="mt-thanks-actions">
          <a :href="`tel:${agent.telephone}`" class="mt-btn mt-btn-primary mt-btn-lg">
            <i class="fas fa-phone-alt text-white"></i> Appeler {{ agent.prenom }} maintenant
          </a>
          <button type="button" class="mt-btn mt-btn-ghost" @click="currentStep = 'search'">
            <i class="fas fa-arrow-left"></i> Retour aux offres
          </button>
        </div>
      </div>
    </section>
  </main>

  <!-- ============== FOOTER ============== -->
  <footer class="mt-footer">
    <div class="mt-footer-inner">
      <div class="mt-footer-brand">
        <strong>L'Assureur Français</strong>
        <span class="mt-footer-copy">Copyright {{ year }}</span>
      </div>
      <div class="mt-footer-meta">
        <a href="tel:0800211211" class="mt-footer-phone">
          <i class="fas fa-phone-alt"></i> 0800 211 211
        </a>
        <span class="mt-footer-sep" aria-hidden="true">—</span>
        <span class="mt-footer-item">
          Intermédiaire en assurance sous le n°
          <a href="https://www.orias.fr/home/showIntermediaire/443503784"
             target="_blank" rel="noopener" class="mt-footer-link">
            Orias 07 022 530
          </a>
        </span>
        <span class="mt-footer-sep" aria-hidden="true">—</span>
        <a href="https://assureur-francais.fr/mentions-legales/"
           target="_blank" rel="noopener" class="mt-footer-link">
          Mentions légales
        </a>
        <span class="mt-footer-sep" aria-hidden="true">—</span>
        <span class="mt-footer-item">
          <i class="fas fa-map-marker-alt"></i>
          145 rue de Charonne, 75011 Paris
        </span>
      </div>
    </div>
  </footer>

  <!-- ============== STICKY BOTTOM BAR (sélection) ============== -->
  <transition name="mt-slide-up">
    <div class="mt-bottombar" v-if="selected.length > 0 && currentStep === 'search'">
      <div class="mt-bottombar-inner">
        <!-- Bloc info gauche (compteur + prix min) — visible mobile + desktop -->
        <div class="mt-bottombar-info">
          <div class="mt-bottombar-count">
            <i class="fas fa-shopping-bag"></i>
            <span><strong>{{ selected.length }}</strong> offre{{ selected.length > 1 ? 's' : '' }} sélectionnée{{ selected.length > 1 ? 's' : '' }}</span>
          </div>
          <div class="mt-bottombar-total">
            à partir de <strong>{{ $api.price(minSelectedPrice) }}/mois</strong>
          </div>
        </div>

        <!-- Mini-cards horizontales (visibles uniquement desktop ≥ 880px) -->
        <div class="mt-bottombar-cards" :class="{ 'is-scrollable': selected.length >= 4 }">
          <transition-group name="mt-bottombar-card-anim" tag="div" class="mt-bottombar-cards-track">
            <div v-for="o in selected" :key="o.id" class="mt-bottombar-card">
              <button type="button" class="mt-bottombar-card-remove"
                      @click="addQuote(o)"
                      :aria-label="`Retirer ${o.produit}`">
                <i class="fas fa-times"></i>
              </button>
              <span class="mt-bottombar-card-formule">{{ o.formule }}</span>
              <span class="mt-bottombar-card-name">{{ o.produit }}</span>
              <span class="mt-bottombar-card-price">
                <strong>{{ $api.price(o.tarif) }}</strong>
                <small>/ mois</small>
              </span>
            </div>
          </transition-group>
        </div>

        <button type="button" class="mt-btn mt-btn-primary mt-btn-lg mt-bottombar-cta" @click="currentStep = 'devis'">
          Recevoir mon devis <i class="fas fa-arrow-right text-white"></i>
        </button>
      </div>
    </div>
  </transition>

  <!-- ============== MODAL : MES BESOINS ============== -->
  <b-modal v-model="showNeedsModal" hide-header hide-footer centered size="lg"
           body-class="p-0" content-class="mt-modal-content" @hidden="onNeedsModalClose">
    <div class="mt-modal" v-if="tempFilters">
      <header class="mt-modal-head">
        <div>
          <h2 class="mt-modal-title"><i class="fas fa-sliders-h"></i> Vos besoins en santé</h2>
          <p class="mt-modal-sub">
            Indiquez l'importance de chaque poste pour personnaliser vos offres.
          </p>
        </div>
        <button type="button" class="mt-modal-close" @click="cancelNeeds" aria-label="Fermer">
          <i class="fas fa-times"></i>
        </button>
      </header>

      <div class="mt-modal-body">
        <p class="mt-modal-intro">
          Indiquez le niveau de remboursement souhaité pour chaque poste de santé.
          Plus le niveau est élevé, meilleurs sont les remboursements&nbsp;— et plus la cotisation peut augmenter.
        </p>
        <div class="mt-needs">
          <div class="mt-need" v-for="(k, i) in Object.keys(tempFilters)" :key="i">
            <div class="mt-need-head">
              <div class="mt-need-icon"><i :class="tempFilters[k].icon"></i></div>
              <span class="mt-need-label">{{ k }}</span>
            </div>
            <div class="mt-need-levels" role="group" :aria-label="`Niveau ${k}`">
              <button type="button"
                      v-for="lvl in 4" :key="lvl"
                      class="mt-need-level"
                      :class="{ active: tempFilters[k].value === lvl }"
                      @click="setTempLevel(k, lvl)">
                {{ ['Mini','Moyen','Fort','Maxi'][lvl-1] }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <footer class="mt-modal-footer">
        <button type="button" class="mt-btn mt-btn-link" @click="resetTempNeeds">
          <i class="fas fa-undo"></i> Réinitialiser
        </button>
        <div class="mt-modal-footer-actions">
          <button type="button" class="mt-btn mt-btn-ghost" @click="cancelNeeds">
            Annuler
          </button>
          <button type="button" class="mt-btn mt-btn-primary mt-btn-lg" @click="applyNeeds">
            <i class="fas fa-check"></i> Valider mes besoins
          </button>
        </div>
      </footer>
    </div>
  </b-modal>

  <!-- ============== MODAL CTA MUTUALISÉE (Souscrire / Intéressé / Rappel) ============== -->
  <b-modal v-model="ctaModal.open" hide-header hide-footer centered
           body-class="p-0" content-class="mt-modal-content"
           :no-close-on-backdrop="callbackLoading"
           :no-close-on-esc="callbackLoading"
           @hidden="ctaModal.offer = null; ctaModal.success = false">
    <div class="mt-cta-modal">
      <button type="button" class="mt-cta-close" @click="closeCtaModal" aria-label="Fermer">
        <i class="fas fa-times"></i>
      </button>

      <!-- ÉTAT SUCCÈS -->
      <template v-if="ctaModal.success && ctaModalContent">
        <div class="mt-cta-icon mt-cta-icon-success" style="background: #00A85E;">
          <i class="fas fa-check text-white" style="font-size: 2rem;"></i>
        </div>
        <h2 class="mt-cta-title">{{ ctaModalContent.successTitle }}</h2>
        <p class="mt-cta-message">
          Votre demande a bien été enregistrée.<br/>
          <strong>{{ agent.prenom }}</strong>, votre conseiller dédié,
          {{ ctaModalContent.successMessage }}
        </p>
      </template>

      <!-- ÉTAT INVITATION / CHARGEMENT -->
      <template v-else-if="ctaModalContent">
        <div class="mt-cta-icon">
          <i :class="ctaModalContent.icon" class="text-white" style="font-size: 2rem;"></i>
        </div>
        <h2 class="mt-cta-title">{{ ctaModalContent.title }}</h2>
        <p class="mt-cta-message">{{ ctaModalContent.message }}</p>
      </template>

      <!-- Carte conseiller (commune aux deux états) -->
      <div class="mt-cta-advisor">
        <b-img :src="$api.link(agent.avatar)" rounded="circle"
               alt="Conseiller" class="mt-cta-advisor-avatar" />
        <div class="mt-cta-advisor-info">
          <span class="mt-cta-advisor-name">{{ agent.prenom }}</span>
          <span class="mt-cta-advisor-role">Votre conseiller dédié</span>
        </div>
        <a :href="`tel:${agent.telephone}`" class="mt-cta-advisor-phone">
          <i class="fas fa-phone-alt"></i> {{ agent.telephone }}
        </a>
      </div>

      <!-- Bandeau d'envoi (uniquement pendant l'appel API) -->
      <div v-if="callbackLoading" class="mt-cta-loading">
        <i class="fas fa-spinner fa-spin"></i>
        <span>Envoi de votre demande au conseiller…</span>
      </div>

      <!-- Actions : bouton unique, libellé adapté à l'état -->
      <div class="mt-cta-actions">
        <button type="button"
                class="mt-btn mt-btn-block"
                :class="ctaModal.success ? 'mt-btn-primary mt-btn-lg' : 'mt-btn-ghost'"
                :disabled="callbackLoading"
                @click="closeCtaModal">
          <i v-if="ctaModal.success" class="fas fa-check text-white"></i>
          {{ ctaModal.success ? "J'ai compris" : 'Fermer' }}
        </button>
      </div>
    </div>
  </b-modal>

  <!-- Widget conseiller (avatar + bulle d'invite + panneau actions) -->
  <AdvisorChat :agent="agent" />
</div>
</template>

<script>
// Délai avant application du budget saisi (voir onPriceInput).
const PRICE_DEBOUNCE_MS = 400;

import LoaderWidget from './LoaderWidget.vue';
import AdvisorChat from './AdvisorChat.vue';
export default {
  components: { LoaderWidget, AdvisorChat },
  data() {
    return {
      year: new Date().getFullYear(),
      price_load: true,
      currentStep: "infos",
      initialClientSnapshot: null,
      // Snapshot des seuls champs qui impactent le calcul tarifaire.
      // Utilisé pour ne relancer getQuotes() que si l'un d'eux a changé.
      initialTarifSnapshot: null,
      showNeedsModal: false,
      tempFilters: null,
      priceMin: null,
      priceMax: null,
      priceBounds: { min: 0, max: 500 },
      params: {},
      client: false,
      filterMenu: false,
      agent: {
        nom: 'Dupont',
        prenom: 'Jean',
        email: 'jean.dupont@company.fr',
        telephone: '06 12 34 56 78',
        whatsapp: '33612345678'
      },
      filters: {
        'Hospitalisation': { icon: "fas fa-hospital", value: null },
        'Soins courants':  { icon: "fas fa-briefcase-medical", value: null },
        'Dentaire':        { icon: "fas fa-tooth", value: null },
        'Optique':         { icon: "fas fa-glasses", value: null },
      },
      tplFilters: [
        { titre: "Mini",       desc: "Couverture essentielle",   value: 1 },
        { titre: "Equilibrée", desc: "Le bon équilibre",          value: 2 },
        { titre: "Maxi",       desc: "Couverture maximale",       value: 4 }
      ],
      sort: { key: "tarif", value: "ASC" },
      datatable: {
        currentPage: 1,
        limitPage: 100,
        totalRow: 0,
        data: []
      },
      selected: [],
      mutuelles: [],
      quoteForm: {
        budget: null,
        hasInsurance: null,
        optin: false,
        wantsCallback: false,
        callbackPhone: null,
        subscribeIntent: false
      },
      loadQuote: false,
      loadQuoteDone: false,
      pendingSearchAfterLoader: false,
      sendLoad: false,
      wantsSpouse: null,
      wantsChildren: null,
      callbackLoading: false,
      // Modale CTA mutualisée (Souscrire / Je suis intéressé / Être rappelé)
      // - open : visibilité globale
      // - offer / type : contexte de la demande
      // - success : bascule du contenu en confirmation (icône check, etc.)
      ctaModal: {
        open: false,
        offer: null,
        type: null, // 'souscription' | 'interet' | 'rappel'
        success: false,
      },
      cityOptions: [],
      cityLoading: false,
      cityTimer: null,
      priceTimer: null,
      appliedPrice: { min: null, max: null },
      showCitySuggestions: false,
    }
  },
  computed: {
    stepIndex() {
      const order = { 'infos': 0, 'search': 1, 'devis': 2, 'merci': 3 };
      return order[this.currentStep] != null ? order[this.currentStep] : 0;
    },
    phoneState() {
      const v = this.client.telephone_1 || '';
      if (v.length === 0) return null;
      return v.length === 10 && /^[0-9]+$/.test(v);
    },
    phoneError() {
      const v = this.client.telephone_1 || '';
      if (v.length === 0) return '';
      if (v.length !== 10) return 'Le numéro doit contenir 10 chiffres';
      if (!/^[0-9]+$/.test(v)) return 'Le numéro ne doit contenir que des chiffres';
      return '';
    },
    emailState() {
      const v = (this.client.email || '').trim().toLowerCase();
      if (v.length === 0) return null;

      // Structure de base : quelquechose@domaine
      const basicRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!basicRegex.test(v)) return false;

      const domain = v.split('@')[1];

      // Fournisseurs grand public les plus courants en France
      const knownDomains = [
        'gmail.com',
        'googlemail.com',
        'outlook.com', 'outlook.fr',
        'hotmail.com', 'hotmail.fr',
        'live.com', 'live.fr',
        'msn.com',
        'yahoo.com', 'yahoo.fr',
        'orange.fr', 'wanadoo.fr',
        'sfr.fr', 'neuf.fr', 'cegetel.net',
        'free.fr', 'aliceadsl.fr',
        'bbox.fr', 'bouyguestelecom.fr',
        'laposte.net',
        'numericable.fr',
        'protonmail.com', 'proton.me',
        'icloud.com', 'me.com', 'mac.com',
        'gmx.fr', 'gmx.com',
      ];

      // 1. Le domaine correspond exactement à un fournisseur connu → valide
      if (knownDomains.includes(domain)) return true;

      // 2. Le domaine ressemble à un fournisseur connu mais avec un TLD
      //    différent (ex: gmail.f, gmail.fr, outlook.co) → invalide
      const domainName = domain.split('.')[0]; // "gmail" dans "gmail.f"
      const knownNames = new Set(knownDomains.map(d => d.split('.')[0]));
      if (knownNames.has(domainName)) return false;

      // 3. Fournisseur inconnu → validation générique plus stricte :
      //    TLD d'au moins 2 lettres, uniquement alphabétique
      const strictRegex = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/;
      return strictRegex.test(v);
    },
    emailError() {
      const v = this.client.email || '';
      if (v.length === 0) return '';
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!regex.test(v)) return "L'adresse e-mail est invalide";
      return '';
    },
    budgetError() {
      if (this.quoteForm.budget === null || this.quoteForm.budget === '') return '';
      if (this.quoteForm.budget > 999) return 'Le montant maximum est 999€';
      return '';
    },
    budgetState() {
      if (this.quoteForm.budget === null || this.quoteForm.budget === '') return null;
      return this.quoteForm.budget < 999;
    },
    iconClient() {
      if (this.client.enfants.length > 0) return 'fas fa-users';
      if (this.client.conjoint.date_naissance) return 'fas fa-user-friends';
      return 'fas fa-user';
    },
    adherents() {
      let res = [];
      if (this.client.conjoint.regime_id != null) {
        res.push({ type: "Conjoint", index: 0, icon: "fas fa-user", date_naissance: this.client.conjoint.date_naissance, regime_id: this.client.conjoint.regime_id });
      }
      this.client.enfants.forEach((e, i) => {
        res.push({ type: "Enfant", index: i, icon: "fas fa-child", date_naissance: e.date_naissance, regime_id: e.regime_id });
      })
      return res;
    },
    titre() {
      let titre = `${this.client.nom} ${this.client.prenom}`;
      let sub = "";
      if (this.client.conjoint.regime_id != null) sub += "Conjoint";
      if (this.client.enfants.length > 0) {
        sub += (sub.length > 0 ? " + " : "") + `${this.client.enfants.length} enfant${this.client.enfants.length > 1 ? 's' : ''}`;
      }
      if (sub.length > 0) titre += ` (${sub})`;
      return titre;
    },
    limitAgeEnfant() {
      return this.$api.moment().subtract(26, 'years').format("YYYY-MM-DD");
    },
    limitAgeAdulte() {
      return this.$api.moment().subtract(18, 'years').format("YYYY-MM-DD");
    },
    minSelectedPrice() {
      if (this.selected.length === 0) return 0;
      return Math.min(...this.selected.map(s => parseFloat(s.tarif) || 0));
    },
    // Le maximum sous le minimum n'est plus corrigé en silence (cela réécrivait un
    // champ que le prospect venait de saisir) : on l'explique, il tranche lui-même.
    priceRangeInvalid() {
      const min = this.priceMin, max = this.priceMax;
      return typeof min === 'number' && !isNaN(min)
          && typeof max === 'number' && !isNaN(max)
          && max < min;
    },
    activeFiltersCount() {
      return Object.keys(this.filters).filter(k => this.filters[k].value !== null).length;
    },
    hasBottombar() {
      return this.selected.length > 0 && this.currentStep === 'search';
    },
    // Configuration des 3 types d'invitation à rappel.
    // - title / message / icon : écran d'invitation (avant clic API)
    // - successTitle / successMessage : écran de confirmation (après API ok)
    //   personnalisés pour que chaque CTA garde sa promesse commerciale
    ctaModalContent() {
      const prenom = (this.client && this.client.prenom) || '';
      const map = {
        souscription: {
          title: 'Souscrire',
          icon: 'fas fa-handshake',
          message: 'Votre conseiller vous rappelle pour souscrire simplement et rapidement.',
          successTitle: `Excellent choix, ${prenom} !`,
          successMessage: 'va vous rappeler très rapidement pour vous accompagner dans votre souscription en toute simplicité.',
        },
        interet: {
          title: 'Je suis intéressé',
          icon: 'fas fa-star',
          message: 'Votre conseiller vous rappelle pour vous donner plus de détails.',
          successTitle: `Bonne nouvelle, ${prenom} !`,
          successMessage: 'va vous rappeler pour vous donner toutes les précisions sur cette offre et répondre à vos questions.',
        },
        rappel: {
          title: 'Être rappelé',
          icon: 'fas fa-phone-alt',
          message: 'Votre conseiller vous rappelle dans les plus brefs délais.',
          successTitle: `C'est noté, ${prenom} !`,
          successMessage: 'va vous rappeler dans les plus brefs délais pour échanger avec vous.',
        },
      };
      return map[this.ctaModal.type] || null;
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        if (this.currentStep === 'search') this.search();
      }
    },
    'client.cp'(newCp) {
      this.searchVilleByCp(newCp);
    },
    currentStep() {
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    },
  },
  methods: {
    search() {
      this.price_load = true;
      let filters = {};
      Object.keys(this.filters).forEach(k => {
        filters["site." + k] = this.filters[k].value;
      });
      const range = this.priceRange();
      filters['min_tarif'] = range.min === null ? null : range.min * 0.85;
      filters['max_tarif'] = range.max === null ? null : range.max * 1.15;
      let params = {
        filters: filters,
        sort: this.sort,
        page: this.datatable.currentPage,
        limit: this.datatable.limitPage
      }
      this.$api.ajax(`/parcour/search?api=${this.$route.params.api}`, params, res => {
        this.datatable = res;
        this.price_load = false;
      });
    },
    age(date) {
      date = this.$api.moment(date);
      return this.$api.moment().diff(date, 'years');
    },
    checkQuote(quote) {
      return !!this.selected.find(s => s.id === quote.id);
    },
    addQuote(quote) {
      let check = this.selected.find(s => s.id === quote.id);
      if (check) {
        this.selected = this.selected.filter(n => n.id != quote.id);
        return false;
      } else {
        this.selected.push(quote);
      }
    },
    regime(id) {
      let regime = this.params.Regime ? this.params.Regime.find(r => r.id === id) : null;
      return regime ? regime.titre : '';
    },
    setSort() {
      this.sort.value = this.sort.value == 'DESC' ? 'ASC' : 'DESC';
      this.search();
    },
    clientHasChanged() {
      if (!this.initialClientSnapshot) return true;
      return JSON.stringify(this.client) !== this.initialClientSnapshot;
    },
    // Extraction des seuls champs qui impactent le calcul tarifaire.
    // Les autres champs (nom, prénom, email, téléphone…) peuvent changer
    // sans nécessiter de recalculer les tarifs des offres.
    tarifSnapshot() {
      const conjoint = this.client && this.client.conjoint ? this.client.conjoint : {};
      const enfants = Array.isArray(this.client && this.client.enfants) ? this.client.enfants : [];
      return JSON.stringify({
        cp: this.client.cp,
        date_effet: this.client.date_effet,
        date_naissance: this.client.date_naissance,
        regime_id: this.client.regime_id,
        conjoint: {
          date_naissance: conjoint.date_naissance || null,
          regime_id: conjoint.regime_id || null,
        },
        enfants: enfants.map(e => ({
          date_naissance: (e && e.date_naissance) || null,
          regime_id: (e && e.regime_id) || null,
        })),
      });
    },
    tarifHasChanged() {
      if (!this.initialTarifSnapshot) return true;
      return this.tarifSnapshot() !== this.initialTarifSnapshot;
    },
    submitForm() {
      // Validation explicite de l'adresse e-mail (regex plus stricte que HTML5)
      if (this.emailState !== true) {
        return;
      }
      localStorage.checkFirst = this.$route.params.api;
      // Rien n'a changé du tout → on passe simplement aux offres
      if (!this.clientHasChanged()) {
        this.currentStep = 'search';
        if (this.datatable.totalRow === 0 && !this.price_load) {
          this.search();
        }
        return;
      }
      // On mémorise avant l'appel pour décider si getQuotes est nécessaire.
      // La comparaison utilise le snapshot pris au mount (ou au dernier submit).
      const shouldRefreshQuotes = this.tarifHasChanged();
      this.$api.ajax(`/parcour/update?api=${this.$route.params.api}`, this.client, res => {
        if (res.status) {
          this.initialClientSnapshot = JSON.stringify(this.client);
          this.initialTarifSnapshot = this.tarifSnapshot();
          this.currentStep = 'search';
          if (shouldRefreshQuotes) {
            // Un champ tarifaire a bougé → recalcul complet côté backend
            this.getQuotes();
          } else {
            // Seuls des champs non-tarifaires ont changé (nom, email, tél…)
            // → on relance juste la recherche si aucune offre n'est déjà chargée
            if (this.datatable.totalRow === 0 && !this.price_load) {
              this.search();
            }
          }
        }
      })
    },
    submitQuoteRequest() {
      if (this.sendLoad) return false;
      this.sendLoad = true;
      let quote_id = this.selected.map(s => s.id);
      let params = {
        client: this.client,
        quote: { ...this.quoteForm, selected: quote_id }
      }
      this.$api.ajax(`/parcour/quote?api=${this.$route.params.api}`, params, res => {
        if (res.status) this.currentStep = 'merci';
        this.sendLoad = false;
      })
    },
    openNeedsModal() {
      this.tempFilters = JSON.parse(JSON.stringify(this.filters));
      this.showNeedsModal = true;
    },
    setTempLevel(key, lvl) {
      this.tempFilters[key].value = this.tempFilters[key].value === lvl ? null : lvl;
    },
    resetTempNeeds() {
      Object.keys(this.tempFilters).forEach(k => { this.tempFilters[k].value = null; });
    },
    applyNeeds() {
      this.filters = this.tempFilters;
      this.showNeedsModal = false;
    },
    cancelNeeds() {
      this.showNeedsModal = false;
    },
    onNeedsModalClose() {
      this.tempFilters = null;
    },
    // Applique le budget pendant la frappe, sans attendre que le prospect quitte
    // le champ. `change` ne se déclenche qu'au blur ou sur Entrée : la liste
    // restait figée pendant la saisie, donnant l'impression que le filtre n'était
    // pas pris en compte. Le délai évite une requête par caractère.
    onPriceInput() {
      if (this.priceTimer) clearTimeout(this.priceTimer);
      this.priceTimer = setTimeout(() => {
        this.priceTimer = null;
        this.applyPriceFilter();
      }, PRICE_DEBOUNCE_MS);
    },
    // Blur ou Entrée : on applique immédiatement, en annulant la frappe en attente.
    // Aucune normalisation ici : le contenu du champ n'est JAMAIS réécrit, sans quoi
    // vider le champ pour ressaisir y ferait apparaître un 0.
    onPriceMinChange() {
      if (this.priceTimer) { clearTimeout(this.priceTimer); this.priceTimer = null; }
      this.applyPriceFilter();
    },
    onPriceMaxChange() {
      if (this.priceTimer) { clearTimeout(this.priceTimer); this.priceTimer = null; }
      this.applyPriceFilter();
    },
    // Fourchette transmise à la recherche : un nombre, ou `null` si le champ est
    // vide — l'API attend l'un ou l'autre pour min_tarif / max_tarif. Une valeur
    // négative est transmise telle quelle, c'est une saisie volontaire.
    priceRange() {
      const num = (v) => (typeof v === 'number' && !isNaN(v) ? v : null);
      return { min: num(this.priceMin), max: num(this.priceMax) };
    },
    // Enregistre la fourchette et relance la recherche, sauf si elle n'a pas bougé
    // depuis la dernière application : `change` se déclenche au blur même quand le
    // debounce vient d'appliquer la même valeur, ce qui doublait la requête.
    applyPriceFilter() {
      const range = this.priceRange();
      if (this.appliedPrice.min === range.min && this.appliedPrice.max === range.max) {
        return;
      }
      this.appliedPrice = range;
      this.saveBudgetToStorage();
      this.search();
    },
    // Persistance du budget dans localStorage — restauré au refresh.
    // Clé indexée par le token API pour ne pas mélanger plusieurs prospects
    // qui utiliseraient le même navigateur.
    budgetStorageKey() {
      return `parcour_budget_${this.$route.params.api || 'default'}`;
    },
    saveBudgetToStorage() {
      try {
        if (typeof window === 'undefined' || !window.localStorage) return;
        window.localStorage.setItem(
          this.budgetStorageKey(),
          JSON.stringify(this.appliedPrice)
        );
      } catch (e) { /* localStorage indisponible / quota — silencieux */ }
    },
    loadBudgetFromStorage() {
      try {
        if (typeof window === 'undefined' || !window.localStorage) return;
        const raw = window.localStorage.getItem(this.budgetStorageKey());
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (typeof parsed.min === 'number' && !isNaN(parsed.min) && parsed.min >= 0) {
          this.priceMin = parsed.min;
        }
        if (typeof parsed.max === 'number' && !isNaN(parsed.max) && parsed.max >= 0) {
          this.priceMax = parsed.max;
        }
        // Garantie de cohérence si un des deux est incohérent
        if (this.priceMin > this.priceMax) this.priceMax = this.priceMin;
      } catch (e) { /* JSON corrompu ou stockage indisponible — silencieux */ }
    },
    coverageLabel(level) {
      const labels = ['—', 'Mini', 'Moyen', 'Fort', 'Maxi'];
      return labels[level || 0];
    },
    getQuotes() {
      if (this.loadQuote) return;
      this.loadQuote = true;
      this.loadQuoteDone = false;
      this.pendingSearchAfterLoader = false;
      this.$api.ajax(`/parcour/price?api=${this.$route.params.api}`, null, res => {
        // L'API a répondu : on signale au loader d'accélérer vers 100 %.
        // Le démontage et le search() suivront sur l'event @finished.
        this.pendingSearchAfterLoader = !!res.status;
        this.loadQuoteDone = true;
      });
    },
    onLoaderFinished() {
      this.loadQuote = false;
      this.loadQuoteDone = false;
      if (this.pendingSearchAfterLoader) {
        this.pendingSearchAfterLoader = false;
        this.search();
      }
    },
    setWantsSpouse(val) {
      this.wantsSpouse = val;
      if (val === false) {
        this.client.conjoint.regime_id = null;
        this.client.conjoint.date_naissance = null;
      } else if (val === true && this.client.conjoint.regime_id === null) {
        this.client.conjoint.regime_id = 1;
      }
    },
    setWantsChildren(val) {
      this.wantsChildren = val;
      if (val === false) {
        this.client.enfants = [];
      } else if (val === true && this.client.enfants.length === 0) {
        this.client.enfants.push({ date_naissance: null, regime_id: null });
      }
    },
    setNeedLevel(key, lvl) {
      this.filters[key].value = this.filters[key].value === lvl ? null : lvl;
    },
    // Ordre canonique des documents d'une offre :
    // 1. Tableau des garanties (clé technique "Plaquette")
    // 2. Bulletin d'adhésion
    // 3. IPID
    // 4. Conditions générales
    // 5. Assistance
    // #JESSY55 : vérifier les libellés exacts des clés en base (Plaquette / Bulletin / IPID / CG / Assistance)
    orderedDocs(docs) {
      if (!Array.isArray(docs)) return [];
      // const order = ['Plaquette', 'Bulletin', 'IPID', 'Conditions', 'Assistance'];
      const rank = (key) => {
        if (!key) return 999;
        const k = String(key).toLowerCase();
        if (k.includes('plaquette') || k.includes('garantie')) return 0;
        if (k.includes('bulletin') || k.includes('adh')) return 1;
        if (k.includes('ipid')) return 2;
        if (k.includes('condition') || k === 'cg') return 3;
        if (k.includes('assistance')) return 4;
        return 5;
      };
      return docs
        .filter(d => d && d.value != null)
        .slice()
        .sort((a, b) => rank(a.key) - rank(b.key));
    },
    // Construit le résumé des garanties d'une offre à partir de garantie.
    // - garantie : objet datatable.data[i].garantie
    // - Pour chaque entrée de params.ResumeGarantie {a: clé groupe, b: clé ligne}
    //   on récupère la ligne correspondante et on renvoie son {key, value}.
    // Renvoie un array de { key: string, value: string }.
    // Helpers d'affichage du résumé : tronque les valeurs longues pour
    // éviter les débordements ; un title natif sert de tooltip.
    isLong(value) {
      return typeof value === 'string' && value.length > 20;
    },
    truncate(value, max = 20) {
      if (typeof value !== 'string' || value.length <= max) return value;
      return value.slice(0, max).trimEnd() + '…';
    },
    getResume(garantie) {
      if (!garantie || !Array.isArray(this.params.ResumeGarantie)) {
        return [];
      }
      const res = [];
      const mapper = {
        "Honoraires chirurgie (DPTAM)": "Hospitalisation OPTAM",
        "Médecins (DPTAM)": "Soins courants OPTAM",
        "Monture + 2 verres très complexes (classe B)": "Optique",
        "Prothèses dentaires remboursées": "Dentaire"
      };
      this.params.ResumeGarantie.forEach(elt => {
        const groupe = garantie[elt.a];
        if (groupe != undefined) {
          const ligne = Object.values(groupe).find(f => f.key === elt.b);
          // On exclut les lignes vides ("-") : le client ne veut voir
          // que les garanties effectivement couvertes par l'offre.
          if (ligne != undefined && ligne.value !== '-' && mapper[ligne.key]) {
            res.push({key: mapper[ligne.key], value: ligne.value});
          }
        }
      });
      return res;
    },
    docLabel(key) {
      if (!key) return '';
      const k = String(key).toLowerCase();
      if (k.includes('plaquette') || k.includes('garantie')) return 'Tableau des garanties';
      if (k.includes('bulletin') || k.includes('adh')) return "Bulletin d'adhésion";
      if (k.includes('ipid')) return 'IPID';
      if (k.includes('condition') || k === 'cg') return 'Conditions générales';
      if (k.includes('assistance')) return 'Assistance';
      return key;
    },
    // Lance la demande de rappel ET ouvre/réutilise la modale CTA mutualisée.
    // - Si la modale n'est pas déjà ouverte, on l'ouvre avec le bon contexte.
    // - Pendant l'appel : `callbackLoading` → spinner d'envoi dans la modale.
    // - Sur succès : la même modale bascule en mode `success` (icône check, message).
    // - Sur échec : la modale reste sur l'écran d'invitation, l'utilisateur peut réessayer.
    requestCallback(o, type = null) {
      if (this.callbackLoading) return;
      this.ctaModal.offer = o;
      this.ctaModal.type = type;
      this.ctaModal.success = false;
      this.ctaModal.open = true;
      this.callbackLoading = true;
      let produit = `${o.mutuelle} - ${o.produit} - ${o.formule}`;
      this.$api.ajax(`/parcour/appels?api=${this.$route.params.api}`, {produit, type}, res => {
        this.callbackLoading = false;
        if (res.status === true) {
          this.ctaModal.success = true;
        }
      })
    },
    closeCtaModal() {
      this.ctaModal.open = false;
      // success est remis à false au prochain requestCallback (cf. ci-dessus)
    },
    // Sélection / retrait de l'offre dans le panier avec animation "flying"
    // vers l'icône panier de la sticky bottombar.
    toggleQuoteWithAnimation(o, evt) {
      const wasSelected = this.checkQuote(o);
      // Si déjà sélectionnée → simple retrait sans animation
      if (wasSelected) {
        this.addQuote(o);
        return;
      }
      // Sinon : animation puis ajout
      this.flyToCart(evt);
      this.addQuote(o);
    },
    // Crée un petit élément flottant qui voyage du bouton vers la bottombar
    flyToCart(evt) {
      try {
        const btn = evt && evt.currentTarget;
        if (!btn) return;
        const btnRect = btn.getBoundingClientRect();
        const targetEl = document.querySelector('.mt-bottombar-count i')
          || document.querySelector('.mt-bottombar');
        const targetRect = targetEl
          ? targetEl.getBoundingClientRect()
          : { left: window.innerWidth - 40, top: window.innerHeight - 40, width: 0, height: 0 };
        const startX = btnRect.left + btnRect.width / 2;
        const startY = btnRect.top + btnRect.height / 2;
        const endX = targetRect.left + targetRect.width / 2;
        const endY = targetRect.top + targetRect.height / 2;
        const dx = endX - startX;
        const dy = endY - startY;

        // 1. BURST initial : un cercle qui s'étend depuis le bouton (effet "boum")
        const burst = document.createElement('div');
        burst.className = 'mt-fly-burst';
        burst.style.left = startX + 'px';
        burst.style.top = startY + 'px';
        document.body.appendChild(burst);
        setTimeout(() => {
          if (burst && burst.parentNode) burst.parentNode.removeChild(burst);
        }, 600);

        // 2. SAC volant : trajectoire en arc, rotation, glow, scale
        const node = document.createElement('div');
        node.className = 'mt-fly-cart';
        node.innerHTML = '<i class="fas fa-shopping-bag"></i>';
        node.style.left = startX + 'px';
        node.style.top = startY + 'px';
        document.body.appendChild(node);

        // Point d'inflexion pour la trajectoire arquée (le sac monte avant de descendre)
        const arcLift = Math.min(180, Math.max(80, Math.abs(dy) * 0.4));
        const midX = startX + dx * 0.5;
        const midY = startY + dy * 0.5 - arcLift;

        // Animation custom via Web Animations API → contrôle précis de la courbe
        node.animate(
          [
            { transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1, offset: 0 },
            { transform: `translate(${midX - startX}px, ${midY - startY}px) scale(1.25) rotate(180deg)`, opacity: 1, offset: 0.5 },
            { transform: `translate(${dx}px, ${dy}px) scale(.35) rotate(360deg)`, opacity: 0, offset: 1 },
          ],
          { duration: 900, easing: 'cubic-bezier(.6, .05, .25, 1)', fill: 'forwards' }
        );

        // 3. PULSE intense de la bottombar à l'arrivée
        setTimeout(() => {
          if (node && node.parentNode) node.parentNode.removeChild(node);
          const bar = document.querySelector('.mt-bottombar');
          if (bar) {
            bar.classList.add('mt-bottombar-pulse');
            setTimeout(() => bar.classList.remove('mt-bottombar-pulse'), 700);
          }
          // Splash à l'arrivée
          if (targetEl) {
            const splash = document.createElement('div');
            splash.className = 'mt-fly-splash';
            splash.style.left = endX + 'px';
            splash.style.top = endY + 'px';
            document.body.appendChild(splash);
            setTimeout(() => {
              if (splash && splash.parentNode) splash.parentNode.removeChild(splash);
            }, 700);
          }
        }, 880);
      } catch (e) { /* fail silently — ne pas casser l'ajout panier */ }
    },
    // Récupère la liste des villes correspondant au code postal saisi.
    // - Déclenché par le watcher sur client.cp
    // - Debounce 300ms pour ne pas spammer l'API pendant la frappe
    // - Si res.status === true → alimente cityOptions (dédupliqué sur Nom_commune)
    // - Si res.status === false → on ne fait rien (consigne client)
    searchVilleByCp(cp) {
      if (this.cityTimer) clearTimeout(this.cityTimer);
      if (!cp || !/^\d{5}$/.test(cp)) {
        this.cityOptions = [];
        return;
      }
      this.cityTimer = setTimeout(() => {
        this.cityLoading = true;
        this.$api.ajax(`/parcour/cp?api=${this.$route.params.api}&cp=${cp}`, null, res => {
          this.cityLoading = false;
          if (res.status === true) {
            const villes = (res.data || [])
              .map(d => d.Nom_commune)
              .filter(Boolean);
            this.cityOptions = [...new Set(villes)];
          }
        });
      }, 300);
    },
    selectCity(v) {
      this.$set(this.client, 'ville', v);
      this.showCitySuggestions = false;
    },
    // Délai de fermeture pour permettre au @mousedown de se déclencher
    // (sinon le blur masque la liste avant le click)
    onCityBlur() {
      setTimeout(() => { this.showCitySuggestions = false; }, 150);
    }
  },
  beforeMount() {
    if (window.matchMedia("(max-width: 767px)").matches) {
      this.filterMenu = false;
    }
  },
  mounted() {
    this.$api.ajax(`/parcour/me?api=${this.$route.params.api}`, null, res => {
      if (res.status) {
        this.params = res.data.params;
        this.client = res.data.client;
        this.agent = res.data.user;
        this.mutuelles = res.data.mutuelles;
        // Les informations du prospect ont pu être collectées en amont par un
        // conseiller dans le CRM. L'absence de conjoint ou d'enfants est donc une
        // réponse à part entière, pas une absence de réponse : les deux toggles se
        // positionnent sur "Non" au lieu de rester indéterminés, sinon les
        // questions semblent à traiter alors qu'elles l'ont déjà été.
        this.wantsSpouse = !!(this.client.conjoint && this.client.conjoint.regime_id !== null);
        this.wantsChildren = !!(this.client.enfants && this.client.enfants.length > 0);
        if (this.client.ville === undefined) this.$set(this.client, 'ville', '');
        this.initialClientSnapshot = JSON.stringify(this.client);
        this.initialTarifSnapshot = this.tarifSnapshot();
        if (this.client.cp) this.searchVilleByCp(this.client.cp);
        // Restaure le budget depuis localStorage avant la 1re recherche,
        // pour que le prospect retrouve sa fourchette au refresh.
        this.loadBudgetFromStorage();
        this.appliedPrice = this.priceRange();
        this.search();
        if(localStorage.checkFirst && localStorage.checkFirst == this.$route.params.api){
          this.currentStep = 'search'
        }
      }
    })
  }
}
</script>

<style scoped>
@import '../../assets/css/parcour.css';
</style>

<!--
  Bloc NON-scoped : nécessaire pour styler le wrapper Bootstrap
  (.modal-content, .modal-body) qui est créé hors de notre template
  Vue et ne reçoit donc pas l'attribut data-v-xxx du scoped CSS.
-->
<style>
.mt-modal-content {
  border: none !important;
  border-radius: 20px !important;
  overflow: hidden !important;
  box-shadow: 0 20px 60px rgba(20, 40, 80, 0.25) !important;
  background: transparent !important;
}
.mt-modal-content > .modal-body {
  padding: 0 !important;
}
</style>
